import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { useQuery } from '@tanstack/react-query';
import { Badge, Button, Dialog, EmptyState, Field, Input, Section, Select, Spinner, Textarea } from '@zt/ui';
import { ConfirmButton } from '../components/Confirm';
import { api, IdempotencyKey } from '../lib/api';
import { HelpTip, PageFrame } from '../lib/help';
import { useI18n, useT } from '../lib/i18n';
import { fieldError, useApiMutation } from '../lib/mutation';
import { usePermissions, useSession } from '../lib/session';
import type { CustomRole, Member, MemberList, Meta, RoleList, Store } from '../lib/types';

export function useMeta() {
  return useQuery({
    queryKey: ['meta'],
    queryFn: () => api.get<Meta>('/v1/admin/meta'),
    staleTime: Infinity,
  });
}

function useRoleOptions() {
  const t = useT();
  const roles = useQuery({ queryKey: ['roles'], queryFn: () => api.get<RoleList>('/v1/admin/roles') });
  return useMemo(
    () => [
      ...(roles.data?.system.map((r) => ({ key: r.key, label: t(`roles.${r.key}`) })) ?? []),
      ...(roles.data?.custom.map((r) => ({ key: r.key, label: r.name })) ?? []),
    ],
    [roles.data, t],
  );
}

function StoreScopePicker({ value, onChange }: { value: string[]; onChange: (v: string[]) => void }) {
  const t = useT();
  const stores = useQuery({ queryKey: ['stores'], queryFn: () => api.get<Store[]>('/v1/admin/stores') });
  return (
    <div className="flex flex-wrap gap-2">
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          className="accent-[var(--zt-pine)]"
          checked={value.length === 0}
          onChange={() => onChange([])}
        />
        {t('admin.users.allStores')}
      </label>
      {stores.data?.map((s) => (
        <label key={s.id} className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            className="accent-[var(--zt-pine)]"
            checked={value.includes(s.id)}
            onChange={(e) => onChange(e.target.checked ? [...value, s.id] : value.filter((x) => x !== s.id))}
          />
          {s.name}
        </label>
      ))}
    </div>
  );
}

function InviteDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const t = useT();
  const roles = useRoleOptions();
  const [email, setEmail] = useState('');
  const [roleKey, setRoleKey] = useState('read_only');
  const [storeScope, setScope] = useState<string[]>([]);
  // Bayi kapsamı (F7-08): bayi temsilcisinde varsayılan "yalnızca atanan bayiler".
  const [dealerScope, setDealerScope] = useState<'all' | 'assigned' | null>(null);
  const scope = dealerScope ?? (roleKey === 'dealer_rep' ? 'assigned' : 'all');
  const [idem] = useState(() => new IdempotencyKey());
  const [manualLink, setManualLink] = useState<string | null>(null);
  const invite = useApiMutation(
    () =>
      api.post<{ delivery: 'email' | 'manual'; inviteUrl?: string }>(
        '/v1/admin/users/invitations',
        { email, roleKey, storeScope, dealerScope: scope },
        { idempotencyKey: idem },
      ),
    {
      invalidate: [['members']],
      onSuccess: (r) => {
        // E-posta gerçekten gönderilmediyse "gönderildi" denmez; bağlantı elle iletilmek üzere gösterilir.
        if (r.delivery === 'manual' && r.inviteUrl) setManualLink(r.inviteUrl);
        else {
          toast.success(t('admin.users.inviteSent'));
          onOpenChange(false);
        }
      },
      silentFieldErrors: true,
    },
  );
  if (manualLink) {
    return (
      <Dialog
        open={open}
        onOpenChange={onOpenChange}
        title={t('admin.users.manualTitle')}
        description={t('admin.users.manualLead', { email })}
        closeLabel={t('common.close')}
        footer={
          <Button variant="primary" onClick={() => onOpenChange(false)}>
            {t('common.done')}
          </Button>
        }
      >
        <div className="space-y-3">
          <code
            className="block break-all rounded-sm border border-line bg-surface-2 p-3 text-sm"
            data-testid="invite-link"
          >
            {manualLink}
          </code>
          <div className="flex items-center gap-1">
            <Button
              onClick={async () => {
                await navigator.clipboard.writeText(manualLink);
                toast.success(t('common.copied'));
              }}
            >
              {t('admin.users.copyLink')}
            </Button>
            <HelpTip k="users.copy_invite_link" />
          </div>
        </div>
      </Dialog>
    );
  }
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={t('admin.users.inviteTitle')}
      closeLabel={t('common.close')}
      footer={
        <>
          <Button onClick={() => onOpenChange(false)}>{t('common.cancel')}</Button>
          <Button
            variant="primary"
            loading={invite.isPending}
            onClick={() => invite.mutate(undefined)}
            disabled={!email}
          >
            {t('admin.users.invite')}
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        <Field
          label={t('common.email')}
          required
          error={
            fieldError(invite.error, 'email') ??
            (invite.error?.code === 'already_member' ? invite.error.title : undefined)
          }
        >
          {(p) => <Input {...p} type="email" value={email} onChange={(e) => setEmail(e.target.value)} />}
        </Field>
        <Field label={t('admin.users.role')} required help={<HelpTip k="users.change_role" />}>
          {(p) => (
            <Select {...p} value={roleKey} onChange={(e) => setRoleKey(e.target.value)}>
              {roles.map((r) => (
                <option key={r.key} value={r.key}>
                  {r.label}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <p className="text-sm text-ink-2">
          {roleKey.startsWith('custom_') ? '' : t(`roleDescriptions.${roleKey}`)}
        </p>
        <div className="space-y-2">
          <div className="flex items-center gap-1">
            <p className="text-sm font-medium">{t('admin.users.storeScope')}</p>
            <HelpTip k="users.store_scope" />
          </div>
          <StoreScopePicker value={storeScope} onChange={setScope} />
        </div>
        <Field
          label={t('admin.users.dealerScope')}
          help={<HelpTip k="users.dealer_scope" />}
          error={fieldError(invite.error, 'dealerScope')}
        >
          {(p) => (
            <Select
              {...p}
              value={scope}
              onChange={(e) => setDealerScope(e.target.value as 'all' | 'assigned')}
            >
              <option value="all">{t('admin.users.dealerScopes.all')}</option>
              <option value="assigned">{t('admin.users.dealerScopes.assigned')}</option>
            </Select>
          )}
        </Field>
      </div>
    </Dialog>
  );
}

function MemberRow({ m, manage, selfId }: { m: Member; manage: boolean; selfId?: string }) {
  const t = useT();
  const { formatRelative } = useI18n();
  const roles = useRoleOptions();
  const update = useApiMutation(
    (body: Record<string, unknown>) => api.patch(`/v1/admin/users/${m.id}`, body, { ifMatch: m.version }),
    {
      invalidate: [['members']],
      success: t('common.saved'),
    },
  );
  const remove = useApiMutation(() => api.del(`/v1/admin/users/${m.id}`), {
    invalidate: [['members']],
    success: t('common.saved'),
  });
  return (
    <li className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3">
      <div className="min-w-40 flex-1">
        <p className="font-medium">
          {m.name}{' '}
          {m.userId === selfId && (
            <span className="text-sm font-normal text-ink-3">({t('admin.users.you')})</span>
          )}
        </p>
        <p className="truncate text-sm text-ink-3">{m.email}</p>
      </div>
      <span className="text-sm text-ink-3">
        {t('admin.users.lastLogin')}: {formatRelative(m.lastLoginAt)}
      </span>
      {manage ? (
        <Select
          aria-label={t('admin.users.changeRole')}
          value={m.roleKey}
          onChange={(e) => update.mutate({ roleKey: e.target.value })}
          className="h-9 w-auto min-w-44 text-sm"
        >
          {roles.map((r) => (
            <option key={r.key} value={r.key}>
              {r.label}
            </option>
          ))}
        </Select>
      ) : (
        <span className="text-sm">
          {m.roleKey.startsWith('custom_') ? m.roleKey : t(`roles.${m.roleKey}`)}
        </span>
      )}
      {manage ? (
        <span className="flex items-center gap-1">
          <Select
            aria-label={t('admin.users.dealerScopeOf', { name: m.name })}
            value={m.dealerScope}
            onChange={(e) => update.mutate({ dealerScope: e.target.value })}
            className="h-9 w-auto min-w-44 text-sm"
          >
            <option value="all">{t('admin.users.dealerScopes.all')}</option>
            <option value="assigned">{t('admin.users.dealerScopes.assigned')}</option>
          </Select>
          <HelpTip k="users.dealer_scope" />
        </span>
      ) : (
        m.dealerScope === 'assigned' && <Badge tone="amber">{t('admin.users.dealerScopes.assigned')}</Badge>
      )}
      <Badge tone={m.status === 'active' ? 'moss' : 'slate'}>
        {m.status === 'active' ? t('admin.users.active') : t('admin.users.suspended')}
      </Badge>
      {manage && m.userId !== selfId && (
        <div className="flex items-center gap-1">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => update.mutate({ status: m.status === 'active' ? 'suspended' : 'active' })}
          >
            {m.status === 'active' ? t('admin.users.suspend') : t('admin.users.activate')}
          </Button>
          <HelpTip k="users.suspend" />
          <ConfirmButton
            message={t('admin.users.confirmRemove', { name: m.name })}
            onConfirm={() => remove.mutateAsync(undefined)}
          >
            {t('admin.users.remove')}
          </ConfirmButton>
          <HelpTip k="users.remove" />
        </div>
      )}
    </li>
  );
}

export function UsersPage() {
  const t = useT();
  const { formatDate } = useI18n();
  const { can } = usePermissions();
  const session = useSession();
  const manage = can('org.user.manage');
  const q = useQuery({ queryKey: ['members'], queryFn: () => api.get<MemberList>('/v1/admin/users') });
  const revoke = useApiMutation((id: string) => api.del(`/v1/admin/users/invitations/${id}`), {
    invalidate: [['members']],
  });
  const [open, setOpen] = useState(false);
  return (
    <PageFrame
      helpKey="page.users"
      actions={
        manage && (
          <div className="flex items-center gap-1">
            <Button variant="primary" onClick={() => setOpen(true)}>
              {t('admin.users.invite')}
            </Button>
            <HelpTip k="users.invite" />
          </div>
        )
      }
    >
      {!q.data ? (
        <Spinner label={t('common.loading')} />
      ) : (
        <>
          <Section title={t('admin.users.members')}>
            <ul className="divide-y divide-line rounded-md border border-line bg-surface">
              {q.data.members.map((m) => (
                <MemberRow key={m.id} m={m} manage={manage} selfId={session.data?.user.id} />
              ))}
            </ul>
          </Section>
          <Section title={t('admin.users.pending')}>
            {q.data.invitations.length === 0 ? (
              <p className="text-ink-2">{t('admin.users.noPending')}</p>
            ) : (
              <ul className="divide-y divide-line rounded-md border border-line bg-surface">
                {q.data.invitations.map((i) => (
                  <li key={i.id} className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3">
                    <span className="flex-1 font-medium break-all">{i.email}</span>
                    <span className="text-sm">
                      {i.roleKey.startsWith('custom_') ? i.roleKey : t(`roles.${i.roleKey}`)}
                    </span>
                    <span className="text-sm text-ink-3">
                      {t('admin.users.expires')}: {formatDate(i.expiresAt, false)}
                    </span>
                    {manage && (
                      <span className="flex items-center gap-1">
                        <Button size="sm" variant="ghost" onClick={() => revoke.mutate(i.id)}>
                          {t('admin.users.revoke')}
                        </Button>
                        <HelpTip k="users.revoke_invitation" />
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </Section>
        </>
      )}
      {open && <InviteDialog open={open} onOpenChange={setOpen} />}
    </PageFrame>
  );
}

function PermissionGroups({
  selected,
  onToggle,
  readOnly,
}: {
  selected: string[];
  onToggle?: (key: string, on: boolean) => void;
  readOnly?: boolean;
}) {
  const t = useT();
  const meta = useMeta();
  const groups = useMemo(() => {
    const map = new Map<string, Meta['permissions']>();
    for (const p of meta.data?.permissions ?? []) map.set(p.module, [...(map.get(p.module) ?? []), p]);
    return [...map.entries()];
  }, [meta.data]);
  return (
    <div className="grid gap-x-8 gap-y-5 sm:grid-cols-2">
      {groups.map(([module, perms]) => (
        <fieldset key={module}>
          <legend className="mb-1.5 font-cond text-[0.9375rem] font-semibold">
            {t(`permissionModules.${module}`)}
          </legend>
          <ul className="space-y-1">
            {perms.map((p) => {
              const on = selected.includes(p.key);
              if (readOnly && !on) return null;
              return (
                <li key={p.key} className="flex items-start gap-2 text-sm">
                  {readOnly ? (
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-pine" aria-hidden />
                  ) : (
                    <input
                      type="checkbox"
                      id={`perm-${p.key}`}
                      className="mt-1 accent-[var(--zt-pine)]"
                      checked={on}
                      onChange={(e) => onToggle?.(p.key, e.target.checked)}
                    />
                  )}
                  <label htmlFor={`perm-${p.key}`} className="text-ink-2">
                    {t(`permissions.${p.key}`)}
                    {p.sensitive && (
                      <span className="ms-1.5 text-[0.75rem] font-medium text-amber">
                        {t('admin.roles.sensitive')}
                      </span>
                    )}
                    {p.phase > (meta.data?.implementedPhase ?? 1) && (
                      <span className="ms-1.5 text-[0.75rem] text-ink-3">
                        {t('admin.roles.phaseNote', { phase: p.phase })}
                      </span>
                    )}
                  </label>
                </li>
              );
            })}
          </ul>
        </fieldset>
      ))}
    </div>
  );
}

function CustomRoleDialog({
  role,
  open,
  onOpenChange,
}: {
  role: CustomRole | null;
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const t = useT();
  const meta = useMeta();
  const [name, setName] = useState(role?.name ?? '');
  const [description, setDescription] = useState(role?.description ?? '');
  const [perms, setPerms] = useState<string[]>(role?.permissions ?? []);
  const save = useApiMutation(
    () =>
      role
        ? api.patch(`/v1/admin/roles/${role.id}`, {
            name,
            description: description || null,
            permissions: perms,
            version: role.version,
          })
        : api.post('/v1/admin/roles', { name, description: description || null, permissions: perms }),
    {
      invalidate: [['roles']],
      success: t('common.saved'),
      onSuccess: () => onOpenChange(false),
      silentFieldErrors: true,
    },
  );
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      variant="sheet"
      title={t('admin.roles.createTitle')}
      closeLabel={t('common.close')}
      footer={
        <>
          <Button onClick={() => onOpenChange(false)}>{t('common.cancel')}</Button>
          <Button
            variant="primary"
            loading={save.isPending}
            onClick={() => save.mutate(undefined)}
            disabled={!name || perms.length === 0}
          >
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        <Field
          label={t('admin.roles.name')}
          required
          error={
            fieldError(save.error, 'name') ??
            (save.error?.code === 'role_name_taken' ? save.error.title : undefined)
          }
        >
          {(p) => <Input {...p} value={name} onChange={(e) => setName(e.target.value)} />}
        </Field>
        <Field label={t('admin.roles.description')} optionalLabel={t('common.optional')}>
          {(p) => <Textarea {...p} value={description} onChange={(e) => setDescription(e.target.value)} />}
        </Field>
        {!role && (
          <Field label={t('admin.roles.basedOn')}>
            {(p) => (
              <Select
                {...p}
                defaultValue=""
                onChange={(e) =>
                  setPerms(meta.data?.roles.find((r) => r.key === e.target.value)?.permissions ?? [])
                }
              >
                <option value="">{t('admin.roles.basedOnNone')}</option>
                {meta.data?.roles.map((r) => (
                  <option key={r.key} value={r.key}>
                    {t(`roles.${r.key}`)}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        )}
        <div>
          <p className="mb-3 text-sm font-medium">
            {t('admin.roles.permissions')}{' '}
            <span className="font-normal text-ink-3">
              ({t('admin.roles.permissionCount', { count: perms.length })})
            </span>
          </p>
          <PermissionGroups
            selected={perms}
            onToggle={(k, on) => setPerms(on ? [...perms, k] : perms.filter((x) => x !== k))}
          />
        </div>
      </div>
    </Dialog>
  );
}

export function RolesPage() {
  const t = useT();
  const { can } = usePermissions();
  const manage = can('org.role.manage');
  const q = useQuery({ queryKey: ['roles'], queryFn: () => api.get<RoleList>('/v1/admin/roles') });
  const del = useApiMutation((id: string) => api.del(`/v1/admin/roles/${id}`), {
    invalidate: [['roles']],
    success: t('common.saved'),
  });
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<CustomRole | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  return (
    <PageFrame
      helpKey="page.roles"
      actions={
        manage && (
          <div className="flex items-center gap-1">
            <Button
              variant="primary"
              onClick={() => {
                setEditing(null);
                setOpen(true);
              }}
            >
              {t('admin.roles.createCustom')}
            </Button>
            <HelpTip k="roles.create_custom" />
          </div>
        )
      }
    >
      {!q.data ? (
        <Spinner label={t('common.loading')} />
      ) : (
        <>
          <Section title={t('admin.roles.system')}>
            <ul className="divide-y divide-line rounded-md border border-line bg-surface">
              {q.data.system.map((r) => (
                <li key={r.key} className="px-4 py-3">
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                    <div className="min-w-0 flex-1">
                      <p className="font-medium">{t(`roles.${r.key}`)}</p>
                      <p className="text-sm text-ink-2">{t(`roleDescriptions.${r.key}`)}</p>
                    </div>
                    <span className="tabular text-sm text-ink-3">
                      {t('admin.roles.permissionCount', { count: r.permissions.length })}
                    </span>
                    <Button
                      size="sm"
                      variant="ghost"
                      aria-expanded={expanded === r.key}
                      onClick={() => setExpanded(expanded === r.key ? null : r.key)}
                    >
                      {t('admin.roles.showPermissions')}
                    </Button>
                  </div>
                  {expanded === r.key && (
                    <div className="mt-4 border-t border-line pt-4">
                      <PermissionGroups selected={[...r.permissions]} readOnly />
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </Section>
          <Section title={t('admin.roles.custom')}>
            {q.data.custom.length === 0 ? (
              <EmptyState title={t('admin.roles.emptyCustom')} />
            ) : (
              <ul className="divide-y divide-line rounded-md border border-line bg-surface">
                {q.data.custom.map((r) => (
                  <li key={r.id} className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3">
                    <div className="min-w-0 flex-1">
                      <p className="font-medium">{r.name}</p>
                      {r.description && <p className="text-sm text-ink-2">{r.description}</p>}
                    </div>
                    <span className="tabular text-sm text-ink-3">
                      {t('admin.roles.permissionCount', { count: r.permissions.length })}
                    </span>
                    {manage && (
                      <div className="flex items-center gap-1">
                        <Button
                          size="sm"
                          onClick={() => {
                            setEditing(r);
                            setOpen(true);
                          }}
                        >
                          {t('common.edit')}
                        </Button>
                        <HelpTip k="roles.edit_custom" />
                        <ConfirmButton message={r.name} onConfirm={() => del.mutateAsync(r.id)}>
                          {t('admin.roles.delete')}
                        </ConfirmButton>
                        <HelpTip k="roles.delete_custom" />
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </Section>
        </>
      )}
      {open && (
        <CustomRoleDialog key={editing?.id ?? 'new'} role={editing} open={open} onOpenChange={setOpen} />
      )}
    </PageFrame>
  );
}
